import { DialogComponent, lastInteractionWasKeyboard } from '@theme/dialog';

/**
 * AP Cookie Banner — custom consent bottom sheet backed by Shopify's Customer
 * Privacy API. Replaces the native Shopify banner's UI only: region gating
 * (`shouldShowBanner`), consent storage (`setTrackingConsent`) and the
 * `visitorConsentCollected` event all remain Shopify's. The banner loads the
 * Customer Privacy API independently. The native banner content it mirrors is recorded in
 * docs/maintenance/native-cookie-banner-snapshot.md.
 *
 * The dialog is deliberately non-dismissible: a visitor in a consent region
 * must answer via one of the consent actions. Escape and backdrop clicks are
 * routed by DialogComponent into `closeDialog`, so a single gate there covers
 * every dismissal path.
 */
class ApCookieBanner extends DialogComponent {
  requiredRefs = ['dialog'];

  /** @type {AbortController | null} */
  #listeners = null;

  /** @type {typeof window.Shopify.customerPrivacy | null} */
  #customerPrivacy = null;

  #canClose = false;
  #started = false;

  constructor() {
    super();

    // DialogComponent assigns `closeDialog` as an instance field (not a
    // prototype method), so overriding must wrap the inherited value rather
    // than use `super.closeDialog`.
    const closeDialog = this.closeDialog;
    this.closeDialog = async () => {
      if (!this.#canClose) return;
      return closeDialog();
    };
  }

  connectedCallback() {
    super.connectedCallback();
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;

    // DialogComponent already preventDefaults Escape keydowns; this covers any
    // browser-initiated close request that reaches the dialog directly. A
    // browser-FORCED close (Android back gesture — not preventable without
    // user activation) is recovered by DialogComponent's close-event safety
    // net; the banner stays closed for that page view and, with consent still
    // unset, returns on the next page (fail closed).
    this.refs.dialog.addEventListener('cancel', (event) => event.preventDefault(), { signal });

    // Retry startup as Shopify's feature loader becomes available through
    // content_for_header.
    this.#start();
    if (!this.#started) {
      document.addEventListener('DOMContentLoaded', this.#start, { once: true, signal });
      window.addEventListener('load', this.#start, { once: true, signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    this.#listeners = null;
  }

  #start = () => {
    if (this.#started) return;
    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return;
    this.#started = true;

    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      if (error) return;
      const customerPrivacy = window.Shopify.customerPrivacy;
      if (!customerPrivacy) return;

      this.#customerPrivacy = customerPrivacy;

      if (!this.isConnected || this.refs.dialog.open) return;
      if (!this.#shouldShow(customerPrivacy)) return;

      this.#syncCheckboxes();
      this.showDialog();
    });
  };

  /**
   * In the theme editor the banner renders only via the preview setting, never
   * from region state — an editing merchant must not be consent-walled. On
   * non-live themes (`shopify theme dev` previews) `?ap-cookie-preview` forces
   * it for manual testing from unregulated regions like AU, where
   * `shouldShowBanner()` is always false.
   *
   * @param {typeof window.Shopify.customerPrivacy} customerPrivacy
   * @returns {boolean}
   */
  #shouldShow(customerPrivacy) {
    if (this.hasAttribute('data-design-mode')) return this.hasAttribute('data-preview');

    if (
      this.hasAttribute('data-allow-param-preview') &&
      new URLSearchParams(window.location.search).has('ap-cookie-preview')
    ) {
      return true;
    }

    return customerPrivacy.shouldShowBanner() === true;
  }

  /**
   * Prefill the category checkboxes: ticked unless the visitor has explicitly
   * declined that signal ('no'). Deliberate design direction (2026-07) —
   * pre-ticked boxes over GDPR's stricter unticked-by-default reading; the
   * markup also ships checked so the sync only ever unticks.
   */
  #syncCheckboxes() {
    const consent = this.#customerPrivacy?.currentVisitorConsent?.();
    if (!consent) return;

    const inputs = {
      preferences: this.refs.personalizationInput,
      marketing: this.refs.marketingInput,
      analytics: this.refs.analyticsInput,
    };

    for (const [signal, input] of Object.entries(inputs)) {
      if (input instanceof HTMLInputElement) input.checked = consent[signal] !== 'no';
    }
  }

  showDialog() {
    const { dialog } = this.refs;
    if (dialog.open) return;

    super.showDialog();

    // Mirror the theme's pointer-open focus-ring suppression, which dialog.js
    // applies only to the five native drawer classes. The banner opens on
    // page load with no interaction, so the initial focus lands visibly on
    // the first control; park focus on the dialog and set the suppression
    // attribute instead. DialogComponent removes the attribute on the first
    // keydown, restoring focus rings for keyboard users. This runs in a
    // second rAF because super.showDialog's own rAF clears the attribute.
    requestAnimationFrame(() => {
      if (lastInteractionWasKeyboard()) return;
      dialog.setAttribute('data-suppress-initial-close-focus', '');
      dialog.focus({ preventScroll: true });
    });
  }

  expandSettings() {
    if (this.hasAttribute('data-expanded')) return;

    this.setAttribute('data-expanded', '');
    this.refs.settingsRegion?.removeAttribute('inert');

    // The State 1 copy is hidden now — keep the dialog description in sync.
    this.refs.dialog.setAttribute('aria-describedby', 'ap-cookie-banner-settings-intro');

    requestAnimationFrame(() => {
      this.refs.settingsHeading?.focus({ preventScroll: true });
    });
  }

  acceptAll() {
    this.#applyConsent({ analytics: true, marketing: true, preferences: true, sale_of_data: true });
  }

  declineAll() {
    this.#applyConsent({ analytics: false, marketing: false, preferences: false, sale_of_data: false });
  }

  savePreferences() {
    // sale_of_data is intentionally omitted: the native preferences modal has
    // no checkbox for it either, and Shopify honors Global Privacy Control for
    // that signal on its own.
    this.#applyConsent({
      preferences: this.refs.personalizationInput?.checked === true,
      marketing: this.refs.marketingInput?.checked === true,
      analytics: this.refs.analyticsInput?.checked === true,
    });
  }

  /**
   * @param {Record<string, boolean>} consent
   */
  #applyConsent(consent) {
    const customerPrivacy = this.#customerPrivacy;

    this.#setBusy(true);

    // Watchdog: if the consent request stalls (dropped connection, blocked
    // request), don't trap the visitor behind a disabled modal — dismiss
    // anyway. Unstored consent re-shows the banner on the next page view.
    const watchdog = setTimeout(this.#dismiss, 8000);
    const done = () => {
      clearTimeout(watchdog);
      this.#dismiss();
    };

    if (customerPrivacy && typeof customerPrivacy.setTrackingConsent === 'function') {
      customerPrivacy.setTrackingConsent(consent, done);
    } else {
      done();
    }
  }

  #setBusy(busy) {
    this.refs.dialog.setAttribute('aria-busy', busy ? 'true' : 'false');
    for (const button of this.refs.dialog.querySelectorAll('button')) {
      button.disabled = busy;
    }
  }

  #dismiss = async () => {
    this.#canClose = true;
    try {
      await this.closeDialog();
    } finally {
      this.#canClose = false;
      this.#setBusy(false);
    }
  };
}

if (!customElements.get('ap-cookie-banner')) customElements.define('ap-cookie-banner', ApCookieBanner);
